import { ReactNode, useMemo } from "react";
import { motion, AnimatePresence, Variants, Transition } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useNavigationDirection, isTabPath } from "@/hooks/useNavigationDirection";
import { Capacitor } from "@capacitor/core";

const SPRING: Transition = {
  type: "spring",
  stiffness: 380,
  damping: 38,
  mass: 0.9,
};

const pushVariants: Variants = {
  enter: { x: "100%", opacity: 1 },
  center: { x: 0, opacity: 1 },
  exit: { x: "-30%", opacity: 0.6 },
};

const popVariants: Variants = {
  enter: { x: "-30%", opacity: 0.6 },
  center: { x: 0, opacity: 1 },
  exit: { x: "100%", opacity: 1 },
};

const tabVariants: Variants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
};

const tabTransition: Transition = { duration: 0.12, ease: "easeOut" };

interface Props {
  children: ReactNode;
}

/**
 * Phase 2 - Telegram-grade page transitions.
 * - Push:  slide in from right, previous parallaxes left.
 * - Pop:   slide back to right, previous returns from left.
 * - Tab:   instant cross-dissolve.
 * Web (non-native) keeps a near-instant fade so design stays consistent
 * but doesn't feel laggy in browsers.
 */
export function NativePageTransition({ children }: Props) {
  const location = useLocation();
  const direction = useNavigationDirection();
  const isNative = useMemo(() => {
    try { return Capacitor.isNativePlatform(); } catch { return false; }
  }, []);

  const isTab = isTabPath(location.pathname);
  const useSlide = isNative && !isTab && (direction === "push" || direction === "pop");
  const variants = direction === "pop" ? popVariants : pushVariants;
  const transition = useSlide ? SPRING : tabTransition;
  const activeVariants = useSlide ? variants : tabVariants;

  return (
    <AnimatePresence mode="sync" initial={false}>
      <motion.div
        key={location.pathname}
        variants={activeVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={transition}
        className="w-full min-h-full"
        style={{
          // GPU-accelerated, avoids layout thrash during slide
          willChange: useSlide ? "transform, opacity" : "opacity",
          // Stack pages on top of each other during transition
          ...(useSlide ? { position: "absolute", inset: 0 } : null),
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

export default NativePageTransition;
